import { Plus, Trash2, Star, GripVertical } from "lucide-react";
import { availabilitySv } from "@/lib/adminLabels";

const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-4 py-2.5 text-white font-['Outfit'] text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";
const labelCls = "text-white/50 text-[11px] font-['Outfit'] uppercase tracking-wider block mb-1";

const EMPTY_PACKAGE = {
  name: "",
  price: 0,
  currency: "SEK",
  price_label: "ex moms",
  guests: "",
  items: [""],
  availability: "Available",
  highlight: false,
};

const AVAILABILITY_OPTIONS = ["Available", "Limited", "2 remaining", "3 remaining", "Very Limited", "Sold Out"];

export default function PackageBuilder({ packages, onChange }) {
  const update = (index, field, value) => {
    const next = [...packages];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addPackage = () => {
    onChange([...packages, { ...EMPTY_PACKAGE, items: [""], _key: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, _itemKeys: [`i-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`] }]);
  };

  const removePackage = (index) => {
    if (!window.confirm("Ta bort detta paket?")) return;
    onChange(packages.filter((_, i) => i !== index));
  };

  const toggleHighlight = (index) => {
    const next = packages.map((p, i) => ({ ...p, highlight: i === index ? !p.highlight : false }));
    onChange(next);
  };

  const updateItem = (pkgIndex, itemIndex, value) => {
    const next = [...packages];
    const items = [...next[pkgIndex].items];
    items[itemIndex] = value;
    next[pkgIndex] = { ...next[pkgIndex], items };
    onChange(next);
  };

  const addItem = (pkgIndex) => {
    const next = [...packages];
    const pkg = next[pkgIndex];
    const newKey = `i-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    next[pkgIndex] = { ...pkg, items: [...pkg.items, ""], _itemKeys: [...(pkg._itemKeys || []), newKey] };
    onChange(next);
  };

  const removeItem = (pkgIndex, itemIndex) => {
    const next = [...packages];
    const pkg = next[pkgIndex];
    next[pkgIndex] = {
      ...pkg,
      items: pkg.items.filter((_, i) => i !== itemIndex),
      _itemKeys: (pkg._itemKeys || []).filter((_, i) => i !== itemIndex),
    };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {packages.map((pkg, i) => (
        <div key={pkg._key || `legacy-pkg-${i}`} className={`border p-5 ${pkg.highlight ? 'border-[#C5A059]/50 bg-[#C5A059]/5' : 'border-[#262626]'} transition-colors`} data-testid={`package-editor-${i}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GripVertical size={16} className="text-white/20" />
              <span className="text-white/40 text-xs font-['Outfit'] uppercase tracking-wider">
                Variant {i + 1}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => toggleHighlight(i)} title="Markera som rekommenderat"
                className={`p-1.5 transition-colors ${pkg.highlight ? 'text-[#C5A059]' : 'text-white/20 hover:text-[#C5A059]/60'}`}>
                <Star size={16} fill={pkg.highlight ? "#C5A059" : "none"} />
              </button>
              <button type="button" onClick={() => removePackage(i)} className="p-1.5 text-white/20 hover:text-red-400 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {/* Name & Price row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div className="md:col-span-2">
              <label className={labelCls}>Paketnamn *</label>
              <input value={pkg.name} onChange={e => update(i, "name", e.target.value)} placeholder="t.ex. VIP-paket, Executive Lounge, Corporate Suite" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pris *</label>
              <input type="number" step="0.01" value={pkg.price} onChange={e => update(i, "price", parseFloat(e.target.value) || 0)} placeholder="2890" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prisetikett</label>
              <input value={pkg.price_label} onChange={e => update(i, "price_label", e.target.value)} placeholder="ex moms / person" className={inputCls} />
            </div>
          </div>

          {/* Guests & Availability */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className={labelCls}>Gäster / Kapacitet</label>
              <input value={pkg.guests} onChange={e => update(i, "guests", e.target.value)} placeholder="t.ex. 1 person, Upp till 10 gäster" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Tillgänglighet</label>
              <select value={pkg.availability} onChange={e => update(i, "availability", e.target.value)} className={inputCls}>
                {AVAILABILITY_OPTIONS.map(o => <option key={o} value={o}>{availabilitySv(o)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Valuta</label>
              <select value={pkg.currency} onChange={e => update(i, "currency", e.target.value)} className={inputCls}>
                <option value="SEK">SEK</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          {/* Included items */}
          <div>
            <label className={labelCls}>Vad som ingår</label>
            <div className="space-y-2">
              {(pkg.items || []).map((item, j) => (
                <div key={(pkg._itemKeys && pkg._itemKeys[j]) || `legacy-item-${i}-${j}`} className="flex gap-2">
                  <input value={item} onChange={e => updateItem(i, j, e.target.value)} placeholder="t.ex. Premiumplats, Fri bar, Privat lounge..." className={`${inputCls} flex-1`} />
                  {pkg.items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i, j)} className="px-2 text-white/20 hover:text-red-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => addItem(i)}
                className="text-[#C5A059]/60 hover:text-[#C5A059] text-xs font-['Outfit'] flex items-center gap-1 mt-1 transition-colors">
                <Plus size={12} /> Lägg till rad
              </button>
            </div>
          </div>

          {pkg.highlight && (
            <div className="mt-3 text-[#C5A059] text-[11px] font-['Outfit'] flex items-center gap-1">
              <Star size={12} fill="#C5A059" /> Detta paket är markerat som "Rekommenderat"
            </div>
          )}
        </div>
      ))}

      <button type="button" onClick={addPackage} data-testid="add-package-btn"
        className="w-full py-4 border-2 border-dashed border-[#262626] hover:border-[#C5A059]/40 text-white/40 hover:text-[#C5A059] text-sm font-['Outfit'] flex items-center justify-center gap-2 transition-colors">
        <Plus size={16} /> Lägg till variant
      </button>
    </div>
  );
}
